/**
 * Every crash the product has had, grouped.
 *
 * The screen this exists to replace is "a tester says the app crashed" — which
 * is where the first TestFlight build left us: sign in, `SIGABRT`, and an iOS
 * crash report naming `reportFatal` with the JavaScript message stripped out.
 * There was nothing to read and nothing to fix from.
 *
 * ## Grouped, never listed
 *
 * One bug produces one report per occurrence, and a crash loop produces
 * hundreds. A flat list is unreadable within a day of a bad release, so the
 * list is of *bugs* and the occurrences sit one click inside. The count and the
 * build list are the two figures that decide what to do next: how many people
 * hit it, and whether it is new.
 *
 * ## Admin only, and quietly so
 *
 * The API answers 404 rather than 403 to anyone who is not an admin, so the
 * gate does not confirm the endpoint exists. This screen honours that: a 404 is
 * "not for you", not an error worth showing a stack trace about.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import {
  api, ApiError, fmtSince, type ErrorGroup, type ErrorOccurrence,
} from '../lib/api.js';
import type { ResolvedRange } from '@aegis/api-client';
import { RangePicker } from '../components/RangePicker.js';

/** Which surfaces get their own colour. A crash list is scanned, not read. */
const SURFACE_CLASS: Record<string, string> = {
  web: 'err-web', mobile: 'err-mobile', desktop: 'err-desktop', api: 'err-api',
};

export function ErrorsScreen(): JSX.Element {
  const [groups, setGroups] = useState<readonly ErrorGroup[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [occurrences, setOccurrences] = useState<readonly ErrorOccurrence[]>([]);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [window, setWindow] = useState<ResolvedRange | null>(null);
  // 7 days. `all` is now ninety days of reports, and the question this screen
  // is opened to answer is almost always "did the release I just shipped break
  // something".
  const [range, setRange] = useState('7d');

  const load = useCallback(async (r: string) => {
    try {
      const res = await api.errorGroups({ range: r });
      setGroups(res.groups);
      setWindow(res.range);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) { setDenied(true); return; }
      setError(e instanceof ApiError ? e.message : 'Could not load crash reports.');
    }
  }, []);

  useEffect(() => { void load(range); }, [load, range]);

  const open = useCallback(async (fingerprint: string) => {
    if (selected === fingerprint) { setSelected(null); return; }
    setSelected(fingerprint);
    setOccurrences([]);
    try {
      setOccurrences((await api.errorOccurrences(fingerprint)).reports);
    } catch {
      // The group row already carries the message and the counts. Failing to
      // expand is a worse view of the same bug, not a broken screen.
    }
  }, [selected]);

  if (denied) {
    return (
      <div className="scroll-area">
        <h2>Crash reports</h2>
        <p className="panel-body dim">This is not available on your account.</p>
      </div>
    );
  }

  if (!groups) {
    return (
      <div className="scroll-area">
        <h2>Crash reports</h2>
        <p className="dim">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Crash reports</h2>
        <span className="chip">{groups.length} distinct</span>
      </div>

      <RangePicker value={range} onChange={setRange} resolved={window} />

      <p className="panel-body dim">
        Every unhandled error from the web app, the phone, the desktop shell and
        the API, grouped by where it happened rather than by how many times.
        Nothing here identifies a trader — a report carries a build and a screen,
        and the account is attached only when one happened to be signed in.
        Reports are kept for 90 days.
      </p>

      {groups.length === 0 ? (
        <p className="panel-body">Nothing has crashed. Reports arrive within a
          minute of happening.</p>
      ) : (
        <ul className="err-list">
          {groups.map((g) => (
            <li key={g.fingerprint} className="err-group">
              <button
                type="button"
                className="err-head"
                onClick={() => { void open(g.fingerprint); }}
                aria-expanded={selected === g.fingerprint}
              >
                <span className={`chip ${SURFACE_CLASS[g.surface] ?? ''}`}>{g.surface}</span>
                <span className="err-name">{g.errorName}</span>
                <span className="err-msg">{g.errorMessage}</span>
                {/* Two figures, and they answer different questions: how bad,
                    and how new. A count without the build list cannot tell a
                    regression from a bug that has always been there. */}
                <span className="err-count">{g.occurrences}×</span>
                <span className="err-when">{fmtSince(g.lastSeen)}</span>
              </button>

              <div className="err-meta">
                <span className="err-kind">{g.kind}</span>
                <span>{g.appVersions.join(', ')}</span>
                {g.usersAffected > 0 ? (
                  <span>{g.usersAffected} account{g.usersAffected === 1 ? '' : 's'}</span>
                ) : (
                  <span className="dim">signed out</span>
                )}
                <span className="dim">first seen {fmtSince(g.firstSeen)}</span>
              </div>

              {selected === g.fingerprint && (
                <div className="err-detail">
                  {occurrences.length === 0 ? (
                    <p className="dim">Loading occurrences…</p>
                  ) : occurrences.slice(0, 1).map((o) => (
                    <div key={o.id}>
                      <p className="err-label">MOST RECENT · {o.platform} {o.osVersion ?? ''}
                        {o.buildId ? ` · build ${o.buildId}` : ''}
                        {o.route ? ` · ${o.route}` : ''}</p>
                      {o.stack ? <pre className="err-pre">{o.stack}</pre> : null}

                      {o.breadcrumbs && o.breadcrumbs.length > 0 ? (
                        <>
                          {/* What the user did on the way in. This is usually
                              what turns "cannot read property of undefined"
                              into a reproduction. */}
                          <p className="err-label">LEADING UP TO IT</p>
                          <ol className="err-crumbs">
                            {o.breadcrumbs.map((c, i) => (
                              <li key={`${c.at}-${i}`}>
                                <span className="err-crumb-cat">{c.category}</span>
                                {c.message}
                              </li>
                            ))}
                          </ol>
                        </>
                      ) : null}
                    </div>
                  ))}
                  {occurrences.length > 1 && (
                    <p className="dim err-more">
                      {occurrences.length - 1} earlier occurrence
                      {occurrences.length === 2 ? '' : 's'}, all with this fingerprint.
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
